import React, { useState, useEffect, useMemo } from "react";
import { useNavigationEvent } from "navigation-react";
import Header from "./Header";
import Footer from "./Footer";
import slugify from "./slugify";

const API_URL = "https://proseppos.salcommsystems.click/product-list-api";
const API_BASE = "https://proseppos.salcommsystems.click";
const FALLBACK_LOGO =
  "https://prosepservices.com/_next/static/media/logo.f3c3e836.png";

const PRODUCTS_PER_PAGE = 20;

function CategoryPage({
  categoryMatch,        // e.g. "padlock"
  exact = false,        // true for exact match (Pumps), false for contains
  title,                // "Padlocks"
  subtitle,             // "High-security padlocks from ABUS..."
  searchPlaceholder,    // "Search padlocks..."
}) {
  const { stateNavigator } = useNavigationEvent();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortBy, setSortBy] = useState("default");
  const [priceRange, setPriceRange] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

    // ✅ Set the page title (browser tab)
useEffect(() => {
  document.title = `${title} | Prosep Services`;

  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }
  meta.content = subtitle || "";

  return () => {
    document.title = "Prosep Services";
  };
}, [title, subtitle]);

  useEffect(() => {
    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load products");
        return res.json();
      })
      .then((data) => {
        setProducts(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);


  const filteredProducts = useMemo(() => {
    const match = (categoryMatch || "").toLowerCase();

    let list = products.filter((p) => {
      const cat = (p.field_category || "").toLowerCase().trim();
      return exact ? cat === match : cat.includes(match);
    });

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((p) => p.title?.toLowerCase().includes(q));
    }

    if (priceRange !== "all") {
      list = list.filter((p) => {
        const price = Number(p.field_price) || 0;
        if (priceRange === "0-100") return price >= 0 && price < 100;
        if (priceRange === "100-500") return price >= 100 && price < 500;
        if (priceRange === "500-1000") return price >= 500 && price < 1000;
        if (priceRange === "1000+") return price >= 1000;
        return true;
      });
    }

    if (sortBy === "price-asc") {
      list = [...list].sort(
        (a, b) => Number(a.field_price) - Number(b.field_price)
      );
    } else if (sortBy === "price-desc") {
      list = [...list].sort(
        (a, b) => Number(b.field_price) - Number(a.field_price)
      );
    } else if (sortBy === "name-asc") {
      list = [...list].sort((a, b) =>
        (a.title || "").localeCompare(b.title || "")
      );
    } else if (sortBy === "name-desc") {
      list = [...list].sort((a, b) =>
        (b.title || "").localeCompare(a.title || "")
      );
    }

    return list;
  }, [products, categoryMatch, exact, sortBy, priceRange, searchTerm]);

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const visibleProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, priceRange, searchTerm]);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getImageUrl = (product) =>
    product.field_product_image
      ? `${API_BASE}${product.field_product_image}`
      : FALLBACK_LOGO;

  const isFallback = (product) => !product.field_product_image;

const openProduct = (product) => {
  stateNavigator.navigate("productdetails", {
    category: slugify(product.field_category || "products"),
    productslug: slugify(product.title),
  });
};

  const clearFilters = () => {
    setSortBy("default");
    setPriceRange("all");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    if (currentPage > 3) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  if (loading) {
    return (
      <>
        <Header />
        <section className="cat-page">
          <div className="cat-status">Loading products...</div>
        </section>
        <Footer />
        <style>{catStyles}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <section className="cat-page">
          <div className="cat-status">Could not load products: {error}</div>
        </section>
        <Footer />
        <style>{catStyles}</style>
      </>
    );
  }

  return (
    <>
      <Header />

      <main className="cat-page">
        {/* HERO */}
        <section className="cat-hero">
          <div className="cat-hero-inner">
            <p className="cat-breadcrumb">
              Home / Products / <span>{title}</span>
            </p>
            <h1 className="cat-title">{title}</h1>
            {subtitle && <p className="cat-subtitle">{subtitle}</p>}
          </div>
        </section>

        {/* TOOLBAR */}
        <section className="cat-toolbar">
          <div className="cat-toolbar-inner">
            <div className="cat-count">
              <strong>{filteredProducts.length}</strong>{" "}
              {filteredProducts.length === 1 ? "product" : "products"}
            </div>

            <div className="cat-controls">
              <input
                type="text"
                className="cat-search"
                placeholder={searchPlaceholder || "Search products..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <select
                className="cat-select"
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
              >
                <option value="all">All Prices</option>
                <option value="0-100">Under GH₵100</option>
                <option value="100-500">GH₵100 – GH₵500</option>
                <option value="500-1000">GH₵500 – GH₵1,000</option>
                <option value="1000+">GH₵1,000 and above</option>
              </select>

              <select
                className="cat-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Sort: Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
              </select>

              {(sortBy !== "default" ||
                priceRange !== "all" ||
                searchTerm) && (
                <button className="cat-clear" onClick={clearFilters}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </section>

        {/* GRID */}
        <section className="cat-grid-wrap">
          <div className="cat-grid-inner">
            {filteredProducts.length === 0 ? (
              <div className="cat-empty">
                <p>No products found in this category.</p>
                <button className="cat-clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <div className="cat-grid">
                  {visibleProducts.map((p) => (
                    <div
                      className="cat-card"
                      key={p.nid}
                      onClick={() => openProduct(p)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openProduct(p);
                        }
                      }}
                    >
                      <div className="cat-image">
                        <img
                          src={getImageUrl(p)}
                          alt={p.title}
                          loading="lazy"
                          className={isFallback(p) ? "cat-logo" : ""}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = FALLBACK_LOGO;
                            e.target.classList.add("cat-logo");
                          }}
                        />
                      </div>

                      <h3 className="cat-product-title">{p.title}</h3>
                      <p className="cat-price">
                        GH₵ {Number(p.field_price).toFixed(2)}
                      </p>
                      <p className="cat-sku">SKU: {p.field_sku}</p>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav className="cat-pagination" aria-label="Pagination">
                    <button
                      className="cat-page-btn cat-page-nav"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      ‹ Prev
                    </button>

                    <div className="cat-page-numbers">
                      {getPageNumbers().map((page, i) =>
                        page === "..." ? (
                          <span key={`ellipsis-${i}`} className="cat-page-ellipsis">
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            className={`cat-page-btn ${
                              currentPage === page ? "active" : ""
                            }`}
                            onClick={() => goToPage(page)}
                            aria-label={`Go to page ${page}`}
                            aria-current={
                              currentPage === page ? "page" : undefined
                            }
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className="cat-page-btn cat-page-nav"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      Next ›
                    </button>
                  </nav>
                )}

                {totalPages > 1 && (
                  <p className="cat-page-info">
                    Showing{" "}
                    <strong>
                      {(currentPage - 1) * PRODUCTS_PER_PAGE + 1}–
                      {Math.min(
                        currentPage * PRODUCTS_PER_PAGE,
                        filteredProducts.length
                      )}
                    </strong>{" "}
                    of <strong>{filteredProducts.length}</strong> products
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <style>{catStyles}</style>
    </>
  );
}

const catStyles = `
  .cat-page { width: 100%; background: #ffffff; font-family: Arial, sans-serif; }
  .cat-hero {
    width: 100%; padding: 80px 40px 60px;
    background: linear-gradient(135deg, #001a3d 0%, #002a5c 50%, #0047a3 100%);
    box-sizing: border-box;
  }
  .cat-hero-inner { max-width: 1400px; margin: 0 auto; }
  .cat-breadcrumb { font-size: 13px; color: #b0c4de; margin: 0 0 12px; letter-spacing: 0.3px; }
  .cat-breadcrumb span { color: #ffffff; font-weight: 600; }
  .cat-title { font-size: 44px; font-weight: 900; color: #ffffff; margin: 0 0 14px; letter-spacing: 0.3px; line-height: 1.15; }
  .cat-subtitle { font-size: 16px; line-height: 1.6; color: #dbe5f0; margin: 0; max-width: 700px; }

  .cat-toolbar { width: 100%; border-bottom: 1px solid #e5e7eb; background: #ffffff; position: sticky; top: 0; z-index: 50; }
  .cat-toolbar-inner { max-width: 1400px; margin: 0 auto; padding: 18px 40px; display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap; }
  .cat-count { font-size: 14px; color: #6b7280; }
  .cat-count strong { color: #111; font-weight: 800; }

  .cat-controls { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
  .cat-search { padding: 10px 16px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; font-family: inherit; outline: none; min-width: 220px; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
  .cat-search:focus { border-color: #c8102e; box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.12); }
  .cat-select { padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; font-family: inherit; background: #ffffff; cursor: pointer; outline: none; transition: border-color 0.15s ease; }
  .cat-select:hover, .cat-select:focus { border-color: #c8102e; }
  .cat-clear { padding: 10px 16px; border: 1px solid #c8102e; background: #ffffff; color: #c8102e; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.15s ease; font-family: inherit; }
  .cat-clear:hover { background: #c8102e; color: #ffffff; }

  .cat-grid-wrap { width: 100%; padding: 48px 40px 80px; box-sizing: border-box; }
  .cat-grid-inner { max-width: 1400px; margin: 0 auto; }
  .cat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }

  .cat-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px; display: flex; flex-direction: column; cursor: pointer; transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease; }
  .cat-card:hover { box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08); transform: translateY(-3px); border-color: #d1d5db; }
  .cat-card:focus-visible { outline: 3px solid #c8102e; outline-offset: 2px; }

  .cat-image { width: 100%; height: 240px; background: #f9fafb; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; overflow: hidden; padding: 14px; box-sizing: border-box; }
  .cat-image img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; display: block; }
  .cat-image img.cat-logo { max-width: 70%; max-height: 60%; opacity: 0.85; }

  .cat-product-title { font-size: 14.5px; font-weight: 600; color: #111; margin: 0 0 10px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 40px; }
  .cat-price { font-size: 16px; font-weight: 800; color: #c8102e; margin: 0 0 4px; }
  .cat-sku { font-size: 12px; color: #9ca3af; margin: 0; }

  .cat-pagination { display: flex; justify-content: center; align-items: center; gap: 8px; margin-top: 56px; flex-wrap: wrap; }
  .cat-page-numbers { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; justify-content: center; }
  .cat-page-btn { min-width: 42px; height: 42px; padding: 0 14px; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; color: #111; font-size: 14px; font-weight: 700; font-family: inherit; cursor: pointer; transition: all 0.15s ease; display: inline-flex; align-items: center; justify-content: center; }
  .cat-page-btn:hover:not(:disabled):not(.active) { border-color: #c8102e; color: #c8102e; transform: translateY(-1px); }
  .cat-page-btn.active { background: #c8102e; border-color: #c8102e; color: #ffffff; box-shadow: 0 6px 14px rgba(200, 16, 46, 0.28); cursor: default; }
  .cat-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .cat-page-nav { padding: 0 18px; }
  .cat-page-ellipsis { padding: 0 6px; color: #9ca3af; font-weight: 700; user-select: none; }
  .cat-page-info { text-align: center; color: #6b7280; font-size: 13px; margin: 20px 0 0; }
  .cat-page-info strong { color: #111; font-weight: 700; }

  .cat-status { padding: 120px 40px; text-align: center; color: #6b7280; font-size: 16px; }
  .cat-empty { text-align: center; padding: 80px 20px; color: #6b7280; }
  .cat-empty p { font-size: 16px; margin: 0 0 20px; }

  @media (max-width: 1100px) { .cat-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 800px) {
    .cat-hero { padding: 60px 24px 44px; }
    .cat-title { font-size: 34px; }
    .cat-subtitle { font-size: 15px; }
    .cat-toolbar-inner { padding: 14px 24px; flex-direction: column; align-items: stretch; }
    .cat-controls { flex-direction: column; align-items: stretch; }
    .cat-search, .cat-select, .cat-clear { width: 100%; }
    .cat-grid-wrap { padding: 32px 24px 60px; }
    .cat-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .cat-image { height: 180px; }
    .cat-pagination { gap: 6px; margin-top: 40px; }
    .cat-page-btn { min-width: 38px; height: 38px; font-size: 13px; padding: 0 10px; }
    .cat-page-nav { padding: 0 14px; }
  }
  @media (max-width: 500px) {
    .cat-hero { padding: 50px 20px 36px; }
    .cat-title { font-size: 26px; }
    .cat-grid-wrap { padding: 24px 20px 50px; }
    .cat-grid { gap: 14px; }
    .cat-card { padding: 12px; }
    .cat-image { height: 140px; padding: 10px; }
    .cat-product-title { font-size: 13px; min-height: 34px; }
    .cat-price { font-size: 15px; }
    .cat-sku { font-size: 11px; }
    .cat-pagination { gap: 4px; margin-top: 32px; }
    .cat-page-btn { min-width: 34px; height: 34px; font-size: 12px; padding: 0 8px; border-radius: 6px; }
    .cat-page-nav { padding: 0 10px; font-size: 11px; }
    .cat-page-info { font-size: 12px; margin-top: 14px; }
  }
`;

export default CategoryPage;